import React, { useEffect, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuthContext } from '../../context/AuthContext';
import { getPaymentHistoryApi } from '../../utils/apis';
import { isAuthenticatedViaCookie, getUserInfoFromLocalStorage } from '../../utils/utils';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../linkpayment/Sidebar';
import MobileMenuButton from '../linkpayment/MobileMenuButton';
import '../linkpayment/LinkPayment.css';

interface Transaction {
  id: number;
  amount: number;
  type: string;
  status: string;
  method: string;
  paymentTransId: string;
  initiatedAt: string;
  completedAt: string;
}

interface Payment {
  id: number;
  amount: number;
  currency: string;
  status: string;
  paymentType: string;
  invoiceNumber: string | null;
  dueDate: string | null;
  statementDate: string | null;
  createdAt: string;
  transactions: Transaction[];
}

interface PaymentHistoryResponse {
  payments: Payment[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

const getStatusColor = (status: string) => {
  switch (status.toUpperCase()) {
    case 'PENDING':
      return 'bg-yellow-100 text-yellow-800';
    case 'SUCCESS':
    case 'COMPLETED':
      return 'bg-green-100 text-green-800';
    case 'SCHEDULED':
      return 'bg-blue-100 text-blue-800';
    case 'FAILED':
    case 'CANCELLED':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const PaymentHistory: React.FC = () => {
  const { theme } = useTheme();
  const { isAuthenticated } = useAuthContext();
  const navigate = useNavigate();
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  useEffect(() => {
    if (!isAuthenticated && !isAuthenticatedViaCookie()) {
      navigate('/login');
      return;
    }
    
    // Get user info from local storage
    setUserInfo(getUserInfoFromLocalStorage());
    
    fetchPaymentHistory(currentPage);
  }, [isAuthenticated, navigate, currentPage]);

  const fetchPaymentHistory = async (page: number) => {
    try {
      setLoading(true);
      const response = await getPaymentHistoryApi(page, 20);
      setPaymentHistory(response);
      setError(null);
    } catch (err) {
      setError('Failed to fetch payment history');
      console.error('Error fetching payment history:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !paymentHistory) {
    return (
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Sidebar */}
        <Sidebar 
          userInfo={userInfo} 
          isOpen={isSidebarOpen} 
          onClose={closeSidebar} 
        />
        
        {/* Mobile Header */}
        <div className="lg:hidden bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
          <div className="px-4 py-3 flex items-center justify-between flex-row-reverse">
            <div className="flex items-center">
              <h1 className="text-lg font-bold text-gray-900">St. Luke Hospital</h1>
            </div>
            <MobileMenuButton isOpen={isSidebarOpen} onToggle={toggleSidebar} />
          </div>
        </div>
        
        <div className="w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Sidebar */}
        <Sidebar 
          userInfo={userInfo} 
          isOpen={isSidebarOpen} 
          onClose={closeSidebar} 
        />
        
        {/* Mobile Header */}
        <div className="lg:hidden bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
          <div className="px-4 py-3 flex items-center justify-between flex-row-reverse">
            <div className="flex items-center">
              <h1 className="text-lg font-bold text-gray-900">St. Luke Hospital</h1>
            </div>
            <MobileMenuButton isOpen={isSidebarOpen} onToggle={toggleSidebar} />
          </div>
        </div>
        
        <div className="w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen">
      {/* Sidebar */}
      <Sidebar 
        userInfo={userInfo} 
        isOpen={isSidebarOpen} 
        onClose={closeSidebar} 
      />
      
      <div className="grow">

        {/* Mobile Header */}
        <div className="lg:hidden bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
          <div className="px-4 py-3 flex items-center justify-between flex-row-reverse flex-row-reverse">
            <div className="flex items-center">
              <h1 className="text-lg font-bold text-gray-900">St. Luke Hospital</h1>
            </div>
            <MobileMenuButton isOpen={isSidebarOpen} onToggle={toggleSidebar} />
          </div>
        </div>


        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Your Payment History</h2>
            <div className="text-sm text-gray-600">
              Total: {paymentHistory?.payments.reduce((total, payment) => total + payment.transactions.length, 0) || 0} transactions
            </div>
          </div>

          {paymentHistory && paymentHistory.payments.length > 0 ? (
            <div className="space-y-3">
              {paymentHistory.payments.map((payment) => 
                payment.transactions.map((transaction) => (
                  <div key={transaction.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-3">
                          <h3 className="text-lg font-semibold text-gray-900">
                            ${transaction.amount.toFixed(2)} {payment.currency}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(transaction.status)}`}>
                            {transaction.status}
                          </span>
                          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                            {transaction.type}
                          </span>
                          {/* <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                            {transaction.method}
                          </span> */}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Transaction ID:</span>
                            <div className="mt-1 font-mono text-xs text-gray-900 break-all">
                              {transaction.paymentTransId}
                            </div>
                          </div>
                          <div>
                            <span className="font-medium">Initiated:</span>
                            <div className="mt-1 text-gray-900">
                              {formatDateTime(transaction.initiatedAt)}
                            </div>
                          </div>
                          <div>
                            <span className="font-medium">Completed:</span>
                            <div className="mt-1 text-gray-900">
                              {transaction.completedAt ? formatDateTime(transaction.completedAt) : 'N/A'}
                            </div>
                          </div>
                          <div>
                            <span className="font-medium">Payment ID:</span>
                            <div className="mt-1 text-gray-900">
                              #{payment.id}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="mb-6">
                <svg 
                  className="mx-auto h-16 w-16 text-gray-400" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor" 
                  aria-hidden="true"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={1.5} 
                    d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Payment History</h3>
              <p className="text-gray-500 mb-6">You haven't made any payments yet. Start by making your first payment!</p>
              <button
                onClick={() => navigate('/payments')}
                className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white ${theme["button-bg"]} hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                Make Your First Payment
                <svg className="ml-2 -mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}

          {/* Pagination */}
          {paymentHistory && paymentHistory.totalPages > 1 && (
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, paymentHistory.totalPages) }, (_, i) => {
                    const pageNum = currentPage < 3 ? i : currentPage - 2 + i;
                    if (pageNum >= paymentHistory.totalPages) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          currentPage === pageNum
                            ? `${theme["button-bg"]} text-white`
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= paymentHistory.totalPages - 1}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              
              <div className="text-sm text-gray-600">
                Page {currentPage + 1} of {paymentHistory.totalPages} 
                <span className="ml-2 text-gray-500">
                  ({paymentHistory.payments.reduce((total, payment) => total + payment.transactions.length, 0)} transactions)
                </span>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistory; 